
import { supabase } from "@/integrations/supabase/client";

export interface PerformanceGoal {
  id: string;
  goal_title: string;
  goal_description?: string;
  goal_category: "financial" | "project_delivery" | "customer_impact" | "innovation" | "leadership_behaviors";
  goal_type: "performance" | "behavioral";
  target_value?: string;
  current_progress: number;
  status: "active" | "paused" | "completed" | "cancelled";
  priority: "high" | "medium" | "low";
  start_date: string;
  target_date: string;
  user_id: string;
  supervisor_id?: string;
  created_at: string;
  updated_at: string;
  is_frozen: boolean;
  year: string;
}

export interface CreatePerformanceGoalData {
  goal_title: string;
  goal_description?: string;
  goal_category: "financial" | "project_delivery" | "customer_impact" | "innovation" | "leadership_behaviors";
  goal_type: "performance" | "behavioral";
  target_value?: string;
  current_progress: number;
  status: "active" | "paused" | "completed" | "cancelled";
  priority: "high" | "medium" | "low";
  start_date: string;
  target_date: string;
  user_id: string;
  supervisor_id?: string;
  year: string;
}

export interface UpdatePerformanceGoalData {
  goal_title?: string;
  goal_description?: string;
  goal_category?: "financial" | "project_delivery" | "customer_impact" | "innovation" | "leadership_behaviors";
  goal_type?: "performance" | "behavioral";
  target_value?: string;
  current_progress?: number;
  status?: "active" | "paused" | "completed" | "cancelled";
  priority?: "high" | "medium" | "low";
  start_date?: string;
  target_date?: string;
}

export const performanceService = {
  async createPerformanceGoal(data: CreatePerformanceGoalData): Promise<PerformanceGoal> {
    const { data: goal, error } = await supabase
      .from('performance_goals')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return goal as PerformanceGoal;
  },

  async getPerformanceGoals(userId: string, year: string): Promise<PerformanceGoal[]> {
    const { data: goals, error } = await supabase
      .from('performance_goals')
      .select('*')
      .eq('user_id', userId)
      .eq('year', year)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (goals || []) as PerformanceGoal[];
  },

  async updatePerformanceGoal(id: string, data: UpdatePerformanceGoalData): Promise<PerformanceGoal> {
    const { data: goal, error } = await supabase
      .from('performance_goals')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return goal as PerformanceGoal;
  },

  async deletePerformanceGoal(id: string): Promise<void> {
    const { error } = await supabase
      .from('performance_goals')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getPerformanceGoalById(id: string): Promise<PerformanceGoal | null> {
    const { data: goal, error } = await supabase
      .from('performance_goals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return goal as PerformanceGoal;
  },

  async getPerformanceAppraisals(year: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('performance_appraisals')
      .select('*')
      .eq('year', year)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getPerformanceBands(year: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('performance_bands')
      .select('*')
      .eq('year', year)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getBandDisagreements(year: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('band_disagreements')
      .select('*')
      .eq('year', year)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
};
